import { useState } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, startOfWeek, endOfWeek, addDays, isValid } from 'date-fns';
import { X, MapPinned, Users, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCalendarJobs } from '../../Hooks/useCalendarJobs';

export default function CalendarSection({ currentDate = new Date(), viewMode = 'Month' }) {
    const [selectedEvent, setSelectedEvent] = useState(null);
    const { jobs, isLoading } = useCalendarJobs();

    let daysToShow = [];
    let headerDays = [];

    if (viewMode === 'Month') {
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        const start = startOfWeek(monthStart);
        const end = endOfWeek(monthEnd);
        daysToShow = eachDayOfInterval({ start, end });
        headerDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    } else if (viewMode === 'Week') {
        const weekStart = startOfWeek(currentDate);
        daysToShow = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
        headerDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    } else if (viewMode === 'Day') {
        daysToShow = [currentDate];
        headerDays = [format(currentDate, 'EEEE')];
    }

    const getEventsForDay = (day) => {
        return jobs.filter(job => {
            if (!isValid(job.date)) {
                return false;
            }
            return format(job.date, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd');
        });
    }

    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center bg-white">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="text-sm text-gray-600 mt-4">Loading schedule...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex-1 overflow-auto bg-white">
                <div className="grid text-xs font-medium text-gray-600 border-b border-gray-200 sticky top-0 bg-white z-10"
                    style={{ gridTemplateColumns: `repeat(${headerDays.length}, minmax(0, 1fr))` }}>
                    {headerDays.map(d => (
                        <div key={d} className="py-3 text-center">{d}</div>
                    ))}
                </div>

                <div className="grid text-xs"
                    style={{ gridTemplateColumns: `repeat(${headerDays.length}, minmax(0, 1fr))` }}>
                    {daysToShow.map((day, i) => {
                        const dayEvents = getEventsForDay(day);
                        const isCurrentMonth = isSameMonth(day, currentDate);
                        const isTodayDate = isToday(day);

                        return (
                            <div
                                key={i}
                                className={`border-r border-b border-gray-100 p-2 min-h-24 hover:bg-gray-50 transition ${!isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''
                                    } ${isTodayDate ? 'bg-blue-50 ring-2 ring-blue-500 ring-inset' : ''}`}
                            >
                                <div className={`font-bold text-sm ${isTodayDate ? 'text-blue-600' : 'text-gray-900'}`}>
                                    {format(day, viewMode === 'Day' ? 'EEEE, MMM d' : 'd')}
                                </div>

                                <div className="mt-1 space-y-1">
                                    {dayEvents.length === 0 && <div className="text-gray-300 text-xs">No jobs</div>}
                                    {dayEvents.map((event, idx) => (
                                        <div
                                            key={event.id}
                                            onClick={() => setSelectedEvent(event)}
                                            className={`p-1.5 rounded-md shadow-sm hover:shadow cursor-pointer truncate font-medium text-white
        ${idx % 4 === 0 ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' :
                                                    idx % 4 === 1 ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                                                        idx % 4 === 2 ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
                                                            'bg-gradient-to-r from-orange-500 to-orange-600'}`}
                                            title={event.title}
                                        >
                                            {dayEvents.length > 3 ? `${dayEvents.length} jobs` : event.title}
                                        </div>
                                    ))}
                                    {dayEvents.length > 3 && (
                                        <div className="text-xs text-gray-500 mt-1">
                                            +{dayEvents.length - 3} more
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {selectedEvent && (
                <>
                    <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setSelectedEvent(null)} />
                    <div className="fixed inset-x-0 bottom-0 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-lg w-full bg-white rounded-t-2xl md:rounded-2xl shadow-2xl z-50 p-6 text-sm"
                        onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900">
                                {selectedEvent.client.first_name} {selectedEvent.client.last_name}
                            </h2>
                            <button onClick={() => setSelectedEvent(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <p className="text-gray-500 text-xs mb-1">Service</p>
                                <p className="font-medium text-lg">{selectedEvent.service.name}</p>
                            </div>

                            <div>
                                <p className="text-gray-500 text-xs mb-1 flex items-center gap-1">
                                    <MapPinned className="w-4 h-4" /> Location
                                </p>
                                <p className="font-medium">{selectedEvent.location}</p>
                            </div>

                            <div>
                                <p className="text-gray-500 text-xs mb-1 flex items-center gap-1">
                                    <Calendar className="w-4 h-4" /> Scheduled
                                </p>
                                <p className="font-medium">{selectedEvent.start}</p>
                            </div>

                            {selectedEvent.surveyors.length > 0 && (
                                <div>
                                    <p className="text-gray-500 text-xs mb-2 flex items-center gap-1">
                                        <Users className="w-4 h-4" /> Assigned Surveyors
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedEvent.surveyors.map(s => (
                                            <span key={s.id} className="px-3 py-1.5 bg-gray-100 rounded-full text-xs font-medium">
                                                {s.full_name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <p className="text-gray-500 text-xs mb-1">Job ID</p>
                                <Link to={`/profile/details/jobs/datatables/${selectedEvent.id}`} className="text-emerald-600 font-bold hover:underline">
                                    {selectedEvent.jobId}
                                </Link>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-8">
                            <Link to={`/profile/details/jobs/datatables/${selectedEvent.id}`} className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg font-bold text-center">
                                View Details
                            </Link>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}